import { Box, Typography } from '@mui/material';
import { getTimeRangeOneDay, getTimeRangeOneHour, getTimeRangeOneWeek } from 'helpers/helpers';
import { DateTime } from 'luxon';
import Chart from 'react-apexcharts';
import { useLayoutEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { heatmapColorsArr } from 'types/colors';
import { brandColors } from 'types/colors.js';
const { cleanAndCapitalize } = require('../../helpers/helpers.js');

const TimeInMS = {
  Second: 1000,
  Minute: 1000 * 60,
  Hour: 1000 * 60 * 60,
  Day: 1000 * 60 * 60 * 24,
  Week: 1000 * 60 * 60 * 24 * 7,
  Month: 1000 * 60 * 60 * 24 * 30,
  Year: 1000 * 60 * 60 * 24 * 365,
};
const ONE_DAY = 86400;
const PERCENT_ZERO_COLOR = '#dfdfdf';
const OCCUPANCY_MAX_COLOR = '#2633aa';
const MOTION_COUNTER_MAX_COLOR = '#2633aa';
const SCORE_MEASUREMENTS = new Set(['clean_air_level', 'co2_ratio', 'comfort_level']);
const SHADED_MEASUREMENTS = new Set(['mass_pm_2_5', 'mass_pm_10', 'tvoc', 'tvoc_max']);
const HEATMAP_COLOR_THRESHOLDS = {
  clean_air_level: {
    min: 50,
    max: 100,
    red: [50, 66],
    yellow: [74, 77],
    green: [95, 100],
  },
  co2_ratio: {
    min: 50,
    max: 100,
    red: [50, 66],
    yellow: [74, 77],
    green: [95, 100],
  },
  comfort_level: {
    min: 50,
    max: 100,
    red: [50, 66],
    yellow: [74, 77],
    green: [95, 100],
  },
  co2_max: {
    min: 0,
    max: 50000,
    red: [1200, 50000],
    yellow: [1005, 1100],
    green: [0, 500],
  },
  mass_pm_2_5: {
    min: 0,
    max: 1000,
    red: [35, 1000],
    yellow: [15, 25],
    green: [0, 7],
  },
  mass_pm_10: {
    min: 0,
    max: 1000,
    red: [50, 1000],
    yellow: [25, 35],
    green: [0, 13],
  },
  tvoc_max: {
    min: 0,
    max: 7000,
    red: [1000, 7000],
    yellow: [700, 900],
    green: [0, 250],
  },
};
const COLOR_TRANSITION_STEP = 0.1;
const RANGE_EPSILON = 0.000001;

const CONTINUOUS_COLOR_SCALES = {
  clean_air_level: { max: 100, color: brandColors.GREEN, isPercentage: true },
  co2_ratio: { max: 100, color: brandColors.GREEN, isPercentage: true },
  comfort_level: { max: 100, color: brandColors.GREEN, isPercentage: true },
  mass_pm_2_5: { max: 800, color: brandColors.RED },
  mass_pm_10: { max: 250, color: brandColors.RED },
  tvoc_max: { max: 1050, color: brandColors.RED },
  occupancy: {
    max: 100,
    color: brandColors.BLUE,
    maxColor: OCCUPANCY_MAX_COLOR,
    isPercentage: true,
    zeroColor: PERCENT_ZERO_COLOR,
  },
  motion_counter: { max: 100, color: brandColors.BLUE },
};

const interpolateHexColor = (startColor, endColor, intensity) => {
  const start = startColor.match(/\w\w/g).map(hex => parseInt(hex, 16));
  const end = endColor.match(/\w\w/g).map(hex => parseInt(hex, 16));

  return `#${start
    .map((channel, index) => {
      return Math.round(channel + (end[index] - channel) * intensity)
        .toString(16)
        .padStart(2, '0');
    })
    .join('')}`;
};

const getTransitionRanges = (start, end, startColor, endColor) => {
  const rangeStart = start + RANGE_EPSILON;
  const rangeEnd = end - RANGE_EPSILON;
  const rangeLength = end - start;
  const rangeCount = Math.ceil((rangeEnd - rangeStart) / COLOR_TRANSITION_STEP);

  return Array.from({ length: rangeCount }, (_, index) => {
    const from = rangeStart + index * COLOR_TRANSITION_STEP;
    const to = Math.min(from + COLOR_TRANSITION_STEP, end) - RANGE_EPSILON;
    const midpoint = (from + to) / 2;

    return {
      from,
      to,
      color: interpolateHexColor(startColor, endColor, (midpoint - start) / rangeLength),
    };
  });
};

const getOccupancyColorRanges = (zeroColor = PERCENT_ZERO_COLOR) => [
  { from: 9999, to: 9999, color: brandColors.NULL },
  { from: 0, to: 0, color: zeroColor },
  ...getTransitionRanges(0, 100, '#eef0ff', brandColors.BLUE),
  { from: 100, to: Number.MAX_SAFE_INTEGER, color: OCCUPANCY_MAX_COLOR },
];

const getMotionCounterColorRanges = () => [
  { from: 9999, to: 9999, color: brandColors.NULL },
  { from: 0, to: 0, color: PERCENT_ZERO_COLOR },
  ...getTransitionRanges(0, 100, '#eef0ff', MOTION_COUNTER_MAX_COLOR),
  { from: 100, to: 100, color: MOTION_COUNTER_MAX_COLOR },
];

const getHeatmapColorRanges = ({ min, max, red, yellow, green }) => {
  const isHigherValueBetter = red[0] < yellow[0] && yellow[0] < green[0];

  if (isHigherValueBetter) {
    return [
      { from: -9998, to: min - RANGE_EPSILON, color: brandColors.RED },
      { from: red[0], to: red[1], color: brandColors.RED },
      ...getTransitionRanges(red[1], yellow[0], brandColors.RED, brandColors.YELLOW),
      { from: yellow[0], to: yellow[1], color: brandColors.YELLOW },
      ...getTransitionRanges(yellow[1], green[0], brandColors.YELLOW, brandColors.GREEN),
      { from: green[0], to: green[1], color: brandColors.GREEN },
      { from: max + RANGE_EPSILON, to: 9998, color: brandColors.GREEN },
      { from: 9999, to: 9999, color: brandColors.NULL },
    ];
  }

  return [
    { from: -9998, to: min - RANGE_EPSILON, color: brandColors.GREEN },
    { from: green[0], to: green[1], color: brandColors.GREEN },
    ...getTransitionRanges(green[1], yellow[0], brandColors.GREEN, brandColors.YELLOW),
    { from: yellow[0], to: yellow[1], color: brandColors.YELLOW },
    ...getTransitionRanges(yellow[1], red[0], brandColors.YELLOW, brandColors.RED),
    { from: red[0], to: red[1], color: brandColors.RED },
    { from: max + RANGE_EPSILON, to: Number.MAX_SAFE_INTEGER, color: brandColors.RED },
    { from: 9999, to: 9999, color: brandColors.NULL },
  ];
};

const getThresholdShadedRanges = (greenLimit, redStart, maxValue) => {
  const greenRanges = Array.from({ length: greenLimit }, (_, index) => ({
    from: index,
    to: index + 0.999999,
    color: interpolateHexColor(brandColors.GREEN, '#e6f6ed', index / (greenLimit - 1)),
  }));
  const yellowBandSize = 0.5;
  const yellowBandCount = Math.ceil((redStart - greenLimit) / yellowBandSize);
  const yellowRanges = Array.from({ length: yellowBandCount }, (_, index) => {
    const from = index === 0 ? greenLimit + 0.000001 : greenLimit + index * yellowBandSize;
    const to = Math.min(greenLimit + (index + 1) * yellowBandSize, redStart) - 0.000001;

    return {
      from,
      to,
      color: interpolateHexColor('#fff9e6', brandColors.YELLOW, index / (yellowBandCount - 1)),
    };
  });
  const redRanges = Array.from({ length: maxValue - redStart + 1 }, (_, index) => {
    const from = redStart + index;

    return {
      from,
      to: index === maxValue - redStart ? maxValue : from + 0.999999,
      color: interpolateHexColor(brandColors.RED, brandColors.RED_DARK, index / (maxValue - redStart)),
    };
  });

  return [
    { from: 9999, to: 9999, color: brandColors.NULL },
    { from: -9998, to: -0.000001, color: brandColors.GREEN },
    ...greenRanges,
    { from: greenLimit, to: greenLimit, color: '#e6f6ed' },
    ...yellowRanges,
    ...redRanges,
    { from: maxValue + 0.000001, to: 9998, color: brandColors.RED_DARK },
  ];
};

const getCo2Ranges = () => [
  { from: 9999, to: 9999, color: brandColors.NULL },
  { from: -9998, to: 449.999999, color: brandColors.NULL },
  ...Array.from({ length: 650 }, (_, index) => {
    const from = 450 + index;

    return {
      from,
      to: from + 0.999999,
      color: interpolateHexColor('#ffffff', brandColors.RED, 0.1 + (index / 650) * 0.9),
    };
  }),
  { from: 1100, to: 9998, color: brandColors.RED },
];

const applyPercentageBoundaryColors = (chartContext, maxColor, zeroColor = PERCENT_ZERO_COLOR) => {
  chartContext?.el?.querySelectorAll('.apexcharts-heatmap-rect').forEach(cell => {
    const value = Number(cell.getAttribute('val'));

    if (value === 0) {
      cell.setAttribute('fill', zeroColor);
      cell.setAttribute('color', zeroColor);
    } else if (value >= 100 && value !== 9999) {
      cell.setAttribute('fill', maxColor);
      cell.setAttribute('color', maxColor);
    }
  });
};

const applySensorLabelLinks = (chartContext, sensors) => {
  // ApexCharts adds an empty y-axis label for heatmap alignment before the
  // visible labels. Keep only the labels corresponding to actual series.
  const labels = Array.from(chartContext?.el?.querySelectorAll('.apexcharts-yaxis-label') ?? []).slice(-sensors.length);

  labels.forEach((label, labelIndex) => {
    // ApexCharts renders heatmap y-axis labels in reverse series order. Associate
    // each label by its series index because sensor display names are not unique
    // and may be truncated by ApexCharts.
    const sensorId = sensors[sensors.length - 1 - labelIndex]?.sensor_id;

    if (sensorId == null) {
      return;
    }

    label.style.cursor = 'pointer';
    label.style.textDecoration = 'underline';
    label.style.pointerEvents = 'all';
    label.onclick = event => {
      event.stopPropagation();
      window.open(`/data-explorer/${encodeURIComponent(sensorId)}`, '_blank', 'noopener,noreferrer');
    };
  });
};

const HeatMap = ({ type, timeRangeObj, sensorsList, showLocationColumns = false, isBuildingDemand = false }) => {
  const dispatch = useDispatch();
  const normalizedType = type || '';
  const chartContainerRef = useRef(null);
  const [chartHeight, setChartHeight] = useState(565);
  const [sensorRowLayouts, setSensorRowLayouts] = useState([]);
  let series;

  useLayoutEffect(() => {
    const updateChartHeight = () => {
      if (chartContainerRef.current) {
        setChartHeight(Math.max(0, window.innerHeight - chartContainerRef.current.getBoundingClientRect().top));
      }
    };

    updateChartHeight();
    window.addEventListener('resize', updateChartHeight);

    return () => window.removeEventListener('resize', updateChartHeight);
  }, []);

  // Early validation
  if (!Array.isArray(sensorsList) || sensorsList.length === 0) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height: '400px',
          border: '1px dashed #ccc',
          borderRadius: '8px',
          margin: '20px',
        }}>
        <Typography variant="h6" color="text.secondary">
          No sensor data available
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Sensors list: {JSON.stringify(sensorsList)}
        </Typography>
      </Box>
    );
  }

  const getTimeLabels = () => {
    let now = Math.floor(Date.now() / 1000);
    let resArray = [];

    if (sensorsList.length > 0 && sensorsList[0]?.data) {
      sensorsList[0].data.forEach(measurement => {
        if (measurement && measurement.unix_time) {
          resArray.push(DateTime.fromSeconds(measurement.unix_time).toFormat('dd.MM HH:mm'));
        }
      });
    }

    return resArray;
  };

  const tvocToMass = tvoc => {
    const res = (Math.log(501 - tvoc) - 6.24) * -996.94;
    return Math.round(res);
  };

  const getSeries = () => {
    return sensorsList.map(obj => {
      if (obj) {
        return {
          name: obj.name,
          data: obj.data.map(measurement => {
            const value = Number(measurement?.value);

            return Number.isFinite(value) ? value : 9999;
          }),
        };
      }
    });
  };

  const getContinuousColorScale = measurementType => {
    if (measurementType.endsWith('_justified')) {
      return { max: 100, color: brandColors.BLUE, isPercentage: true };
    }

    if (measurementType.endsWith('_saveable')) {
      return { max: 100, color: brandColors.GREEN, isPercentage: true };
    }

    if (measurementType.endsWith('_requested')) {
      return { max: 100, color: brandColors.RED, isPercentage: true };
    }

    return CONTINUOUS_COLOR_SCALES[measurementType];
  };

  const getRangesColors = measurementType => {
    const heatmapThresholds = HEATMAP_COLOR_THRESHOLDS[measurementType];

    if (heatmapThresholds) {
      return getHeatmapColorRanges(heatmapThresholds);
    }

    switch (measurementType) {
      case 'co2_max':
        return getCo2Ranges();

      case 'tvoc_max':
        return getThresholdShadedRanges(500, 1000, 1050);

      case 'temperature':
        return [
          { from: 9999, to: 9999, color: brandColors.NULL },
          { from: 0, to: 0.1, color: heatmapColorsArr[0] },
          { from: 23, to: 23.9, color: heatmapColorsArr[8] },
          { from: 22, to: 22.9, color: heatmapColorsArr[7] },
          { from: 24, to: 24.9, color: heatmapColorsArr[7] },
          { from: 21, to: 21.9, color: heatmapColorsArr[6] },
          { from: 25, to: 25.9, color: heatmapColorsArr[6] },
          { from: 20, to: 20.9, color: heatmapColorsArr[5] },
          { from: 26, to: 26.9, color: heatmapColorsArr[5] },
          { from: 18, to: 19.9, color: heatmapColorsArr[4] },
          { from: 27, to: 27.9, color: heatmapColorsArr[4] },
          { from: 17, to: 17.9, color: heatmapColorsArr[3] },
          { from: 28, to: 28.9, color: heatmapColorsArr[3] },
          { from: 14, to: 16.9, color: heatmapColorsArr[2] },
          { from: 29, to: 30.9, color: heatmapColorsArr[2] },
          { from: 10, to: 13.9, color: heatmapColorsArr[1] },
          { from: 31, to: 32.9, color: heatmapColorsArr[1] },
          { from: 33, to: 100, color: heatmapColorsArr[0] },
          { from: 0.11, to: 10.9, color: heatmapColorsArr[0] },
        ];

      case 'rh':
        return [
          { from: 9999, to: 9999, color: brandColors.NULL },
          { from: 0, to: 0.1, color: heatmapColorsArr[9] },
          { from: 50, to: 55.9, color: heatmapColorsArr[8] },
          { from: 47, to: 49.9, color: heatmapColorsArr[7] },
          { from: 56, to: 60.9, color: heatmapColorsArr[7] },
          { from: 45, to: 46.9, color: heatmapColorsArr[6] },
          { from: 41, to: 44.9, color: heatmapColorsArr[6] },
          { from: 61, to: 62.9, color: heatmapColorsArr[5] },
          { from: 36, to: 40.9, color: heatmapColorsArr[5] },
          { from: 63, to: 65.9, color: heatmapColorsArr[4] },
          { from: 31, to: 35.9, color: heatmapColorsArr[4] },
          { from: 66, to: 67.9, color: heatmapColorsArr[3] },
          { from: 25, to: 30.9, color: heatmapColorsArr[3] },
          { from: 68, to: 70.9, color: heatmapColorsArr[2] },
          { from: 21, to: 24.9, color: heatmapColorsArr[2] },
          { from: 71, to: 75.9, color: heatmapColorsArr[1] },
          { from: 16, to: 20.9, color: heatmapColorsArr[1] },
          { from: 76, to: 80.9, color: heatmapColorsArr[0] },
          { from: 11, to: 15.9, color: heatmapColorsArr[0] },
          { from: 80, to: 89.9, color: heatmapColorsArr[0] },
          { from: 0.11, to: 10.9, color: heatmapColorsArr[0] },
          { from: 90, to: 100, color: heatmapColorsArr[0] },
        ];

      default:
        return [{ from: 0, to: 100, color: heatmapColorsArr[0] }];
    }
  };

  try {
    series = getSeries();

    if (!series || series.length === 0) {
      return (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            height: '400px',
            border: '1px dashed #ccc',
            borderRadius: '8px',
            margin: '20px',
          }}>
          <Typography variant="h6" color="text.secondary">
            No valid series data available
          </Typography>
        </Box>
      );
    }
  } catch (error) {
    console.error('❌ Error generating series:', error);
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height: '400px',
          border: '1px dashed #ccc',
          borderRadius: '8px',
          margin: '20px',
        }}>
        <Typography variant="h6" color="error">
          Error processing heat map data
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          {error.message}
        </Typography>
      </Box>
    );
  }

  const continuousColorScale = getContinuousColorScale(type);
  const scoreMeasurement = SCORE_MEASUREMENTS.has(type);
  const shadedMeasurement = SHADED_MEASUREMENTS.has(type);
  // Keep compact heatmaps readable: up to five rows use a taller 30px row;
  // larger heatmaps use 20px rows. The extra height is for rotated timestamps.
  const heatmapRowHeight = sensorsList.length <= 5 ? 30 : 20;
  const heatmapHeight = Math.max(sensorsList.length * heatmapRowHeight + 120, Math.min(chartHeight, sensorsList.length * 20));
  const visibleSensors = showLocationColumns ? [...sensorsList].reverse() : [];
  const locationGroups = visibleSensors.reduce((groups, sensor) => {
    const floorNumber = sensor.floor_number;
    const areaName = sensor.area_name || 'Unassigned area';
    const floorKey = String(floorNumber ?? 'unassigned');
    let floorGroup = groups.find(group => group.key === floorKey);

    if (!floorGroup) {
      floorGroup = { key: floorKey, floorNumber, areas: [], sensorCount: 0 };
      groups.push(floorGroup);
    }

    let areaGroup = floorGroup.areas.find(group => group.name === areaName);
    if (!areaGroup) {
      areaGroup = { name: areaName, sensors: [] };
      floorGroup.areas.push(areaGroup);
    }

    areaGroup.sensors.push(sensor);
    floorGroup.sensorCount += 1;
    return groups;
  }, []);
  const syncSensorRowLayouts = chartContext => {
    if (!showLocationColumns || !chartContainerRef.current) return;

    const containerTop = chartContainerRef.current.getBoundingClientRect().top;
    const rows = [];
    chartContext?.el?.querySelectorAll('.apexcharts-heatmap-rect').forEach(cell => {
      const bounds = cell.getBoundingClientRect();
      const top = Math.round(bounds.top - containerTop);
      const height = Math.round(bounds.height);
      const row = rows.find(item => Math.abs(item.top - top) <= 1);

      if (!row) {
        rows.push({ top, height });
      } else {
        row.height = Math.max(row.height, height);
      }
    });

    rows.sort((a, b) => a.top - b.top);
    if (rows.length !== visibleSensors.length) return;

    setSensorRowLayouts(previousRows => {
      const isUnchanged =
        previousRows.length === rows.length &&
        previousRows.every((row, index) => row.top === rows[index].top && row.height === rows[index].height);
      return isUnchanged ? previousRows : rows;
    });
  };
  const percentageMaxColor = continuousColorScale?.isPercentage
    ? (continuousColorScale.maxColor ?? continuousColorScale.color)
    : type === 'rh'
      ? heatmapColorsArr[0]
      : null;
  const percentageZeroColor = isBuildingDemand ? '#eef0ff' : PERCENT_ZERO_COLOR;
  const zeroColor = isBuildingDemand ? '#eef0ff' : continuousColorScale?.zeroColor;
  const isRequestedDemand = normalizedType.endsWith('_requested');
  const options = {
    chart: {
      type: 'heatmap',
      accessibility: {
        enabled: false,
      },
      events: {
        mounted: chartContext => {
          if (percentageMaxColor) {
            applyPercentageBoundaryColors(chartContext, percentageMaxColor, percentageZeroColor);
          }
          if (showLocationColumns) syncSensorRowLayouts(chartContext);
          else applySensorLabelLinks(chartContext, sensorsList);
        },
        updated: chartContext => {
          if (percentageMaxColor) {
            applyPercentageBoundaryColors(chartContext, percentageMaxColor, percentageZeroColor);
          }
          if (showLocationColumns) syncSensorRowLayouts(chartContext);
          else applySensorLabelLinks(chartContext, sensorsList);
        },
      },
    },
    plotOptions: {
      heatmap: {
        ...(continuousColorScale && !scoreMeasurement && !shadedMeasurement && type !== 'occupancy' && type !== 'motion_counter'
          ? { enableShades: !isRequestedDemand, shadeIntensity: 0.5 }
          : { enableShades: false, shadeIntensity: 0 }),
        colorScale:
          continuousColorScale && !scoreMeasurement && !shadedMeasurement
            ? {
                min: continuousColorScale.min ?? 0,
                max: continuousColorScale.max,
                ranges:
                  type === 'occupancy'
                    ? getOccupancyColorRanges(percentageZeroColor)
                    : type === 'motion_counter'
                      ? getMotionCounterColorRanges()
                      : isRequestedDemand
                        ? [
                            { from: 9999, to: 9999, color: brandColors.NULL },
                            { from: 0, to: 0, color: '#eef0ff' },
                            ...getTransitionRanges(0, 15, brandColors.RED_LIGHT, brandColors.RED),
                            { from: 15, to: 100, color: brandColors.RED },
                          ]
                        : [
                            { from: 9999, to: 9999, color: brandColors.NULL },
                            ...(zeroColor ? [{ from: 0, to: 0, color: zeroColor }] : []),
                            ...(continuousColorScale.lowValueColor
                              ? [
                                  {
                                    from: 0.000001,
                                    to: continuousColorScale.lowValueMax,
                                    color: continuousColorScale.lowValueColor,
                                  },
                                ]
                              : []),
                            {
                              from: continuousColorScale.lowValueColor
                                ? continuousColorScale.lowValueMax + 0.000001
                                : zeroColor
                                  ? 0.000001
                                  : (continuousColorScale.min ?? 0),
                              to: continuousColorScale.max,
                              color: continuousColorScale.color,
                            },
                          ],
              }
            : { ranges: getRangesColors(type) },
      },
    },
    responsive: [
      {
        options: {
          chart: {
            width: '100%',
          },
        },
      },
    ],
    legend: {
      show: false,
      // position: 'bottom',
    },
    dataLabels: {
      enabled: false,
    },
    tooltip: {
      x: {
        formatter: function (val) {
          switch (timeRangeObj.label) {
            case '1 Hour':
              // Customize the format of the x-axis tooltip here
              if (val !== '') {
                return getTimeRangeOneHour(val);
              } else {
                return '';
              }
            case 'Today':
              if (val !== '') {
                return getTimeRangeOneDay(val);
              } else {
                return '';
              }
            case '1 Week':
              if (val !== '') {
                return getTimeRangeOneWeek(val);
              } else {
                return '';
              }
            case 'date to date':
              if (val !== '') {
                return getTimeRangeOneWeek(val);
              } else {
                return '';
              }
          }
        },
      },
      y: {
        formatter: function (val) {
          if (
            type === 'occupancy' ||
            type === 'motion_counter' ||
            type === 'clean_air_level' ||
            type === 'rh' ||
            type === 'comfort_level' ||
            type === 'co2_ratio' ||
            normalizedType.endsWith('_justified') ||
            normalizedType.endsWith('_saveable') ||
            normalizedType.endsWith('_requested')
          ) {
            return val === 9999 ? 'N/A' : `${Math.round(val)}%`;
          } else if (type === 'tvoc_max') {
            return val === 9999 ? 'N/A' : val < 1 ? `0 (µg/m³)` : `${val} (µg/m³)`;
          } else if (type === 'mass_pm_2_5' || type === 'mass_pm_10') {
            return `${Math.round(val)} (µg/m³)`;
          } else if (type === 'temperature') {
            return val === 9999 ? 'N/A' : `${Math.round(val)}°`;
          } else if (type === 'co2_max') {
            return val === 9999 ? 'N/A' : `${Math.round(val)} ppm`;
          } else {
            return val === 9999 ? 'N/A' : Math.round(val);
          }
        },
      },
    },
    xaxis: {
      tickAmount: 40,
      categories: getTimeLabels(),
      labels: {
        offsetX: timeRangeObj.value <= TimeInMS.Hour ? 29 : timeRangeObj.value <= TimeInMS.Day ? 17 : 0,
        rotate: -40,
        rotateAlways: true,
        style: {
          fontSize: '12px',
        },
      },
    },
    yaxis: {
      labels: {
        show: !showLocationColumns,
        align: 'left',
        offsetX: 27,
        style: {
          fontSize: '14px',
          fontWeight: 'bold',
        },
      },
    },
  };

  return (
    <Box
      ref={chartContainerRef}
      sx={{
        display: 'flex',
        flexDirection: showLocationColumns ? 'row' : 'column',
        marginTop: '-.2rem',
        width: '100%',
        height: heatmapHeight,
        minHeight: 0,
        // ApexCharts renders tooltips outside the chart grid. Clipping this wrapper
        // hides tooltips for cells in the rightmost columns.
        overflow: 'visible',
      }}>
      {showLocationColumns && (
        <Box
          sx={{
            width: 232,
            height: heatmapHeight,
            flexShrink: 0,
            overflow: 'hidden',
            fontSize: '12px',
            position: 'relative',
          }}>
          {sensorRowLayouts.length === visibleSensors.length &&
            (() => {
              let row = 1;

              const rowStyle = (startRow, rowCount) => {
                const firstRow = sensorRowLayouts[startRow - 1];
                const lastRow = sensorRowLayouts[startRow + rowCount - 2];

                return {
                  height: lastRow.top + lastRow.height - firstRow.top,
                  position: 'absolute',
                  top: firstRow.top,
                };
              };

              return locationGroups.flatMap(floorGroup => {
                const floorStartRow = row;
                const floorCells = floorGroup.areas.flatMap(areaGroup => {
                  const areaStartRow = row;
                  const sensorCells = areaGroup.sensors.map(sensor => {
                    const sensorRow = row;
                    row += 1;

                    return (
                      <Box
                        key={sensor.sensor_id}
                        component="button"
                        type="button"
                        onClick={() =>
                          window.open(`/data-explorer/${encodeURIComponent(sensor.sensor_id)}`, '_blank', 'noopener,noreferrer')
                        }
                        sx={{
                          ...rowStyle(sensorRow, 1),
                          border: 0,
                          borderBottom: '1px solid #fff',
                          background: 'transparent',
                          color: 'inherit',
                          cursor: 'pointer',
                          font: 'inherit',
                          left: 144,
                          overflow: 'hidden',
                          p: 0.25,
                          textAlign: 'left',
                          textOverflow: 'ellipsis',
                          width: 88,
                          whiteSpace: 'nowrap',
                        }}>
                        {sensor.name}
                      </Box>
                    );
                  });

                  return [
                    <Box
                      key={`${floorGroup.key}-${areaGroup.name}`}
                      sx={{
                        ...rowStyle(areaStartRow, areaGroup.sensors.length),
                        alignItems: 'center',
                        borderBottom: '1px solid #fff',
                        borderRight: '1px solid #fff',
                        display: 'flex',
                        left: 52,
                        overflow: 'hidden',
                        p: 0.25,
                        width: 92,
                        wordBreak: 'break-word',
                      }}>
                      {areaGroup.name}
                    </Box>,
                    ...sensorCells,
                  ];
                });

                return [
                  <Box
                    key={floorGroup.key}
                    sx={{
                      ...rowStyle(floorStartRow, floorGroup.sensorCount),
                      alignItems: 'center',
                      borderBottom: '1px solid #fff',
                      borderRight: '1px solid #fff',
                      color: brandColors.BLUE,
                      display: 'flex',
                      fontWeight: 700,
                      left: 0,
                      overflow: 'hidden',
                      p: 0.25,
                      width: 52,
                      wordBreak: 'break-word',
                    }}>
                    {floorGroup.floorNumber == null ? 'Unassigned' : `Floor ${floorGroup.floorNumber}`}
                  </Box>,
                  ...floorCells,
                ];
              });
            })()}
        </Box>
      )}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        {Array.isArray(sensorsList) && <Chart options={options} series={series} type="heatmap" height={heatmapHeight} />}
      </Box>
    </Box>
  );
};

export default HeatMap;
